import {Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {DatePicker, Table} from '@core/components';
import {CompliancePlan, PlanCalendarEntry, RiskControlRow, RiskDeclarationRow, SurveyRow, TaskItem} from '@core/types';
import {PlanService} from '@core/services';
import {Confirm} from '@core/modals';
import {ModalService} from '@core/services';
import moment from 'moment-timezone';

@Component({
  selector: 'core-plan-schedule',
  imports: [
    DatePicker,
    Table,
    ReactiveFormsModule
  ],
  templateUrl: './plan-schedule.html',
  styleUrl: './plan-schedule.scss',
  standalone: true,
  host: {
    style: 'width: 100%;display:flex;flex-direction:column;gap:2rem'
  }
})
export class PlanSchedule implements OnInit, OnChanges {
  @Input() plan: CompliancePlan | null = null;
  @Input() tasks: TaskItem[] = [];
  @Input() calendarEntries: PlanCalendarEntry[] = [];
  @Input() phaseOneSurveyCount = 0;
  @Input() phaseOneDeclarationCount = 0;
  @Input() phaseThreeSummary: {inherit: number; promote: number; finalize: number} = {inherit: 0, promote: 0, finalize: 0};
  @Input() phaseOneSurveys: SurveyRow[] = [];
  @Input() phaseOneDeclarations: RiskDeclarationRow[] = [];
  @Input() phaseThreeControls: RiskControlRow[] = [];
  @Input() phaseThreeDecisions: Array<{controlId: string | number; decision: 'inherit' | 'promote' | 'finalize'}> = [];
  @Output() saved = new EventEmitter<CompliancePlan>();
  form: FormGroup;
  isSubmitting = false;
  errorMessage = '';
  selectedTaskIds = new Set<string | number>();
  private planService = inject(PlanService);
  private modal = inject(ModalService);
  private formBuilder = inject(FormBuilder);

  constructor() {
    this.form = this.formBuilder.group({
      name: ['', [Validators.required]],
      endDate: [null, [Validators.required]],
      description: [''],
      goals: ['']
    });
  }

  ngOnInit() {
    this.patchFormFromPlan();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['plan'] && this.plan) {
      this.patchFormFromPlan();
    }
  }

  toggleTaskSelection(task: TaskItem, checked: boolean) {
    if (checked) {
      this.selectedTaskIds.add(task.id);
    } else {
      this.selectedTaskIds.delete(task.id);
    }
  }

  async submitPlan() {
    if (this.isSubmitting) return;
    if (this.form.invalid || !this.plan?.id) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.form.disable({emitEvent: false});

    const value = this.form.value;
    const successorPayload = {
      name: value.name,
      description: value.description || undefined,
      goals: value.goals || undefined,
      endDate: this.toApiDateTime(value.endDate)
    };

    const confirmed = await this.askForConfirmation(
      'AVISO - CONFIRMAR FASE 4',
      'Al confirmar, el plan actual quedará archivado y se abrirá un nuevo plan con los controles heredados. La fecha de inicio del nuevo plan será hoy. ¿Confirmas el cierre?',
      'Lanzar'
    );
    if (!confirmed) {
      this.isSubmitting = false;
      this.form.enable({emitEvent: false});
      return;
    }

    const createResponse = await this.planService.closeAndOpenPlan(this.plan.id, successorPayload, {skipLoader: true});
    if (!createResponse.success || !createResponse.result) {
      this.isSubmitting = false;
      this.form.enable({emitEvent: false});
      this.errorMessage = createResponse.message || 'No se pudo completar el cierre y apertura del plan.';
      return;
    }

    this.isSubmitting = false;
    this.form.enable({emitEvent: false});
    this.saved.emit(createResponse.result);
  }

  controlDecisionLabel(control: RiskControlRow): string {
    const decision = this.phaseThreeDecisions.find(item => String(item.controlId) === String(control.id))?.decision || 'inherit';
    if (decision === 'promote') return 'Promocionar';
    if (decision === 'finalize') return 'Finalizar';
    return 'Heredar';
  }

  controlOutcomeLabel(control: RiskControlRow): string {
    const decision = this.phaseThreeDecisions.find(item => String(item.controlId) === String(control.id))?.decision || 'inherit';
    if (decision === 'promote') {
      if (String(control.status || '') === 'draft') return 'Se hereda al nuevo plan';
      return 'Pasa a permanente';
    }
    if (decision === 'finalize') return 'Se descarta del ciclo activo';
    return 'Se hereda al nuevo plan';
  }

  private toApiDateTime(value: unknown): string | null {
    if (!value) return null;
    const parsed = moment(value as string);
    if (!parsed.isValid()) return null;
    return parsed.format('YYYY-MM-DDTHH:mm:ssZ');
  }

  private patchFormFromPlan() {
    if (!this.plan) return;
    const defaultEnd = moment().startOf('day').add(1, 'year');
    this.form.patchValue({
      name: this.plan.name || '',
      description: this.plan.description || '',
      goals: this.plan.goals || '',
      endDate: defaultEnd.toISOString()
    });
  }

  private askForConfirmation(title: string, description: string, confirmText: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.modal.open<Confirm, boolean>(Confirm, {
        inputs: {
          title,
          description,
          confirmText,
          cancelText: 'Cancelar',
        },
        outputs: {
          close: () => resolve(false),
          confirm: () => resolve(true),
        },
        options: {size: 'md', closeOnBackdrop: true},
      });
    });
  }
}
